<template>
  <div class="flex gap-3 rounded-[20px] bg-white p-3">
    <UINewCheckboxBox
      v-if="selectable"
      class="pt-2.5"
      :model-value="selected"
      @update:model-value="$emit('toggleSelect')"
    />
    <!-- Канал -->
    <div class="flex min-w-0 flex-1 flex-col gap-3">
      <div class="flex items-start gap-3">
        <div class="relative shrink-0">
          <div
            class="size-[50px] cursor-pointer overflow-hidden rounded-full border-[1.5px] border-bgnew bg-gray-100"
            @click="$emit('openPlace', place.slug)"
          >
            <img
              v-if="place.avatar"
              :src="place.avatar"
              alt="avatar"
              class="size-full object-cover"
            />
          </div>
          <avatar-labels :data="place" class="absolute -bottom-[9px] left-1/2 -translate-x-1/2 !w-auto" />
        </div>
        <div class="min-w-0 flex-1">
          <div
            class="mt-[3px] cursor-pointer truncate text-base font-medium leading-[1.3] text-txmain"
            @click="$emit('openPlace', place.slug)"
          >
            {{ place.name }}
          </div>
          <div class="mt-[3px] flex min-w-0 items-center gap-2">
            <a
              :href="place.url"
              target="_blank"
              rel="nofollow noindex noopener noreferrer"
              class="!w-auto max-w-[200px] truncate text-xs leading-[1.3]"
              :style="{ color: buttonsColor ?? '#3176FF' }"
              >{{ handle }}</a
            >
            <template v-if="place.categories?.length">
              <span class="size-1 shrink-0 rounded-full bg-[#D9E5FF]" />
              <UINewTags :tags="place.categories" size="small" tag-class="border-none !px-0 text-txmain" class="min-w-0" />
            </template>
          </div>
        </div>
      </div>

      <p
        v-if="place.description"
        class="line-clamp-2 text-xs leading-[1.4] text-txmain [overflow-wrap:anywhere]"
      >
        {{ place.description }}
      </p>

      <!-- Статистика: в панели канала — в два ряда, как в мобильной карточке -->
      <div v-if="compact" class="mt-auto grid h-[50px] grid-cols-4 items-center rounded-xl border border-bgnew px-1">
        <div v-for="(stat, index) in stats" :key="stat.label" class="relative flex flex-col items-center leading-[1.5]">
          <span class="text-xs text-[#1B1C1E]/40">{{ stat.label }}</span>
          <span class="text-xs font-medium text-txmain">{{ stat.value }}</span>
          <span v-if="index" class="absolute -left-px top-1/2 h-8 w-px -translate-y-1/2 bg-bgnew" />
        </div>
      </div>
      <div v-else class="mt-auto flex h-[38px] items-center gap-7 rounded-lg border border-bgnew px-3 text-sm leading-[1.5] xl:px-[30px]">
        <div class="flex min-w-0 grow items-center justify-center gap-1.5 whitespace-nowrap">
          <span class="text-sm font-medium text-txmain">{{ fmtNum(place.subscribersStatistic?.count || 0) }}</span>
          <span class="truncate text-sm text-txmain/60">подписчиков</span>
        </div>
        <div class="place-card__stat">
          <span class="text-sm font-medium text-txmain">{{ fmtNum(place.viewsStatistic?.countLast24h || 0) }}</span>
          <span class="truncate text-sm text-txmain/60">просмотров</span>
        </div>
        <div class="place-card__stat !gap-1 text-sm font-medium text-txmain">
          <IMan class="size-4 shrink-0" :style="{ color: $route.name === 'catalog' ? '#3176FF' : buttonsColor ?? '#3176FF' }" />
          {{ place.subscribersStatistic?.malePercent || 0 }}%
          <IWoman class="ml-1 size-4 shrink-0" :style="{ color: $route.name === 'catalog' ? '#E97CFF' : buttonsColor ?? '#3176FF' }" />
          {{ place.subscribersStatistic?.femalePercent || 0 }}%
        </div>
        <div class="place-card__stat">
          <span class="text-sm font-medium text-txmain">{{ place.er || 0 }}%</span>
          <span class="text-sm text-txmain/60">ERR</span>
        </div>
      </div>
    </div>

    <!-- Цена и действия -->
    <div class="flex w-[262px] shrink-0 flex-col gap-4 rounded-2xl bg-bgnew px-[15px] py-[19px]">
      <div class="flex items-center justify-between gap-2">
        <div class="price-select">
          <el-select
            v-if="place.prices.length > 0"
            v-model="place.format"
            placeholder="Выбрать"
            popper-class="price-select__el"
            :disabled="$route.name === 'order'"
          >
            <el-option
              v-for="item in place.prices"
              :key="item.id"
              :label="place.format ? place.format?.name : item.name"
              :value="item"
            >
              <span class="cursor-pointer">{{ item?.name || "-" }}</span>
              <span
                class="cursor-pointer text-right text-base font-bold"
                :style="{ color: buttonsColor ?? '#3176FF' }"
              >
                {{ typeof item?.price === "number" ? new Intl.NumberFormat("ru-RU").format(item.price) : "-" }} ₽
              </span>
            </el-option>
          </el-select>
        </div>
        <span
          class="whitespace-nowrap font-medium leading-[1.3] text-txmain"
          :class="place.format?.price ? 'text-xl' : 'text-sm'"
        >
          {{ place.format?.price ? `${new Intl.NumberFormat("ru-RU").format(place.format.price)} ₽` : "Недоступен" }}
        </span>
      </div>

      <div class="h-px bg-[#1B1C1E]/[0.06]" />

      <div class="mt-auto flex items-center gap-1.5">
        <button
          type="button"
          class="place-card__action"
          :title="place.isFavorite ? 'Убрать из избранного' : 'В избранное'"
          @click="$emit('follow', place)"
        >
          <i
            class="fi fi-sr-heart text-[22px] leading-none transition-colors"
            :class="place.isFavorite ? 'text-[#fd8787]' : 'text-txprimary/60'"
          />
        </button>
        <button
          v-if="canSaveToCollection"
          type="button"
          class="place-card__action"
          title="Сохранить в подборку"
          @click="$emit('saveToCollection', place)"
        >
          <ICollectionAdd class="size-[22px] fill-txprimary/60" />
        </button>
        <button
          type="button"
          class="place-card__action"
          title="Написать каналу"
          @click="$emit('message', place)"
        >
          <i class="fi fi-sr-comment-alt translate-y-0.5 text-xl leading-none text-primary" />
        </button>
        <button
          v-if="!isInBasket"
          type="button"
          :disabled="place.prices.length === 0 || place.format === null"
          class="flex h-11 flex-1 items-center justify-center rounded-xl text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          :style="{ backgroundColor: buttonsColor ?? '#3176FF' }"
          @click="$emit('addToBasket', place)"
        >
          <i class="fi fi-sr-shopping-cart text-[22px] leading-none" />
        </button>
        <div v-else class="flex h-11 flex-1 items-center justify-between rounded-xl bg-white px-3">
          <button type="button" class="flex items-center" @click="$emit('decreaseQuantity', place)">
            <IMinus class="size-4" :style="{ fill: buttonsColor ?? '#3176FF' }" />
          </button>
          <span class="text-sm text-txmain">{{ basketQuantity || 1 }}</span>
          <button type="button" class="flex items-center" @click="$emit('increaseQuantity', place)">
            <IPlus class="size-4" :style="{ fill: buttonsColor ?? '#3176FF' }" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ReducedPlace } from "@/interfaces/publisher";
  import { fmtNum } from "~/common/functions";
  import IPlus from "~/components/UINew/icons/IPlus.vue";
  import IMinus from "~/components/UINew/icons/IMinus.vue";
  import ICollectionAdd from "~/components/UINew/icons/ICollectionAdd.vue";
  import IWoman from "~/components/UINew/icons/IWoman.vue";
  import IMan from "~/components/UINew/icons/IMan.vue";

  interface Props {
    place: ReducedPlace;
    isInBasket: boolean;
    basketQuantity: number;
    buttonsColor?: string;
    // Узкий контейнер (панель канала): статистика в два ряда
    compact?: boolean;
    selectable?: boolean;
    selected?: boolean;
  }

  const props = defineProps<Props>();

  defineEmits<{
    toggleSelect: [];
    saveToCollection: [place: ReducedPlace];
    openPlace: [slug: string];
    addToBasket: [place: ReducedPlace];
    decreaseQuantity: [place: ReducedPlace];
    increaseQuantity: [place: ReducedPlace];
    follow: [place: ReducedPlace];
    message: [place: ReducedPlace];
  }>();

  // Подборки есть не на всех страницах (и не везде выкачены) — без обработчика
  // кнопка ничего бы не делала, поэтому показываем её, только если сохранение слушают
  const canSaveToCollection = !!getCurrentInstance()?.vnode.props?.onSaveToCollection;

  const handle = computed(() =>
    (props.place.url || "").replace("https://t.me/", "@").replace("https:/t.me/", "@")
  );

  const stats = computed(() => {
    const subscribers = props.place.subscribersStatistic;
    return [
      { label: "подписчики", value: fmtNum(subscribers?.count || 0) },
      { label: "просмотры", value: fmtNum(props.place.viewsStatistic?.countLast24h || 0) },
      { label: "ERR", value: `${props.place.er || 0}%` },
      { label: "М/Ж", value: `${subscribers?.malePercent || 0} / ${subscribers?.femalePercent || 0}` },
    ];
  });
</script>

<style scoped lang="scss">
  .place-card__stat {
    @apply relative flex min-w-0 grow items-center justify-center gap-1.5 whitespace-nowrap;

    &::before {
      content: "";
      @apply absolute -left-3.5 top-1/2 h-5 w-px -translate-y-1/2 bg-[#1B1C1E]/[0.06];
    }
  }

  .place-card__action {
    @apply flex size-11 shrink-0 items-center justify-center rounded-xl bg-white transition-colors hover:bg-primary/10;
  }

  .price-select {
    .el-select {
      min-width: auto;
      width: auto;
      height: 32px !important;
      min-height: 32px !important;

      :deep(.el-select__wrapper) {
        border-radius: 6px !important;
        border: 0 !important;
        box-shadow: none !important;
        height: 32px !important;
        min-height: 32px !important;
        padding: 0 7px;
        max-width: 150px;
        background-color: #fff !important;

        &.is-disabled {
          background-color: #4292f41a !important;
          border: 1px solid theme("colors.border") !important;
        }

        .el-select__selected-item.el-select__placeholder {
          position: relative;
          transform: unset;
          cursor: pointer;
        }

        .el-select__selected-item.el-select__placeholder span {
          color: rgba(58, 61, 68, 0.6) !important;
          font-size: 14px !important;
          cursor: pointer;
        }
      }

      :deep(.el-icon.el-select__caret.el-select__icon) {
        svg {
          width: 14px;
          height: 14px;

          path {
            fill: #556d80 !important;
          }
        }

        &:not(:last-child) {
          margin-bottom: 8px;
        }
      }
    }
  }
</style>
